import { useState, useEffect } from "react";
import axios from "axios";
import "./BuildingTypeManagement.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function BuildingTypeManagement() {
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [luseList, setLuseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    applicableZoning: [],
  });
  const [newBuildingType, setNewBuildingType] = useState({
    code: "",
    name: "",
    description: "",
    applicableZoning: [],
  });

  useEffect(() => {
    fetchBuildingTypes();
    fetchLuseList();
  }, []);

  const fetchBuildingTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/building-types`);
      setBuildingTypes(response.data);
    } catch (error) {
      console.error("Error fetching building types:", error);
      alert("載入建築類型失敗。");
    } finally {
      setLoading(false);
    }
  };

  const fetchLuseList = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/zoning/luse-list`);
      setLuseList(response.data || []);
    } catch (error) {
      console.error("Error fetching luse list:", error);
      alert("載入用途區域列表失敗。");
    }
  };

  const handleZoningToggle = (luse, isEditMode = false) => {
    if (isEditMode) {
      const currentZoning = editForm.applicableZoning || [];
      if (currentZoning.includes(luse)) {
        setEditForm({
          ...editForm,
          applicableZoning: currentZoning.filter((z) => z !== luse),
        });
      } else {
        setEditForm({
          ...editForm,
          applicableZoning: [...currentZoning, luse],
        });
      }
    } else {
      const currentZoning = newBuildingType.applicableZoning || [];
      if (currentZoning.includes(luse)) {
        setNewBuildingType({
          ...newBuildingType,
          applicableZoning: currentZoning.filter((z) => z !== luse),
        });
      } else {
        setNewBuildingType({
          ...newBuildingType,
          applicableZoning: [...currentZoning, luse],
        });
      }
    }
  };

  const handleAddBuildingType = async () => {
    if (!newBuildingType.code || !newBuildingType.name) {
      alert("代碼和名稱是必填項目。");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        code: newBuildingType.code,
        name: newBuildingType.name,
        description: newBuildingType.description || undefined,
        applicableZoning:
          newBuildingType.applicableZoning.length > 0
            ? newBuildingType.applicableZoning
            : undefined,
      };
      await axios.post(`${API_BASE_URL}/api/admin/building-types`, payload);
      await fetchBuildingTypes();
      setShowAddForm(false);
      setNewBuildingType({
        code: "",
        name: "",
        description: "",
        applicableZoning: [],
      });
      alert("建築類型已新增。");
    } catch (error) {
      console.error("Error adding building type:", error);
      const errorMessage =
        error.response?.data?.message || "建築類型新增失敗。";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingCode(type.code);
    setEditForm({
      name: type.name || "",
      description: type.description || "",
      applicableZoning: type.applicableZoning || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingCode(null);
    setEditForm({
      name: "",
      description: "",
      applicableZoning: [],
    });
  };

  const handleSaveEdit = async (code) => {
    if (!editForm.name) {
      alert("名稱是必填項目。");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: editForm.name,
        description: editForm.description || undefined,
        applicableZoning:
          editForm.applicableZoning.length > 0
            ? editForm.applicableZoning
            : undefined,
      };
      await axios.patch(`${API_BASE_URL}/api/admin/building-types/${code}`, payload);
      await fetchBuildingTypes();
      setEditingCode(null);
      setEditForm({
        name: "",
        description: "",
        applicableZoning: [],
      });
      alert("修改完成。");
    } catch (error) {
      console.error("Error updating building type:", error);
      const errorMessage =
        error.response?.data?.message || "修改失敗。";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBuildingType = async (code) => {
    if (
      !window.confirm(
        `確定要刪除建築類型"${code}"嗎？\n相關的所有工種單價也會一併刪除。`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/admin/building-types/${code}`);
      await fetchBuildingTypes();
      alert("建築類型已刪除。");
    } catch (error) {
      console.error("Error deleting building type:", error);
      const errorMessage =
        error.response?.data?.message || "建築類型刪除失敗。";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="building-type-management">
      <div className="page-header">
        <h1>建築類型管理</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            if (editingCode) {
              handleCancelEdit();
            }
            setShowAddForm(!showAddForm);
          }}
          disabled={loading}
        >
          {showAddForm ? "取消" : "+ 新增建築類型"}
        </button>
      </div>

      {editingCode && (
        <div className="add-form edit-form">
          <h3>建築類型修改</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                代碼 <span className="required">*</span>
              </label>
              <input
                type="text"
                value={editingCode}
                className="form-input readonly"
                readOnly
                disabled
              />
              <small className="form-hint">代碼無法修改。</small>
            </div>
            <div className="form-group">
              <label>
                名稱 <span className="required">*</span>
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                className="form-input"
                placeholder="例如：獨棟住宅、公寓"
              />
            </div>
            <div className="form-group full-width">
              <label>說明</label>
              <textarea
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    description: e.target.value,
                  })
                }
                className="form-input"
                rows="3"
                placeholder="請輸入建築類型的說明"
              />
            </div>
            <div className="form-group full-width">
              <label>適用用途區域</label>
              <div className="zoning-checkbox-container">
                {luseList.length === 0 ? (
                  <div className="loading-zoning">載入用途區域列表中...</div>
                ) : (
                  <div className="zoning-checkbox-grid">
                    {luseList.map((luse) => (
                      <label key={luse} className="zoning-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editForm.applicableZoning.includes(luse)}
                          onChange={() => handleZoningToggle(luse, true)}
                          className="zoning-checkbox"
                        />
                        <span>{luse}</span>
                      </label>
                    ))}
                  </div>
                )}
                {editForm.applicableZoning.length > 0 && (
                  <div className="selected-zoning">
                    <strong>已選擇的用途區域（{editForm.applicableZoning.length}個）：</strong>
                    <div className="selected-zoning-tags">
                      {editForm.applicableZoning.map((zoning) => (
                        <span key={zoning} className="selected-zoning-tag">
                          {zoning}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={() => handleSaveEdit(editingCode)}
              disabled={loading}
            >
              儲存
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancelEdit}
              disabled={loading}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="add-form">
          <h3>新增建築類型</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                代碼 <span className="required">*</span>
              </label>
              <input
                type="text"
                value={newBuildingType.code}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="form-input"
                placeholder="예: DETACHED, APARTMENT"
              />
            </div>
            <div className="form-group">
              <label>
                名稱 <span className="required">*</span>
              </label>
              <input
                type="text"
                value={newBuildingType.name}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    name: e.target.value,
                  })
                }
                className="form-input"
                placeholder="例如：獨棟住宅、公寓"
              />
            </div>
            <div className="form-group full-width">
              <label>說明</label>
              <textarea
                value={newBuildingType.description}
                onChange={(e) =>
                  setNewBuildingType({
                    ...newBuildingType,
                    description: e.target.value,
                  })
                }
                className="form-input"
                rows="3"
                placeholder="請輸入建築類型的說明"
              />
            </div>
            <div className="form-group full-width">
              <label>適用用途區域</label>
              <div className="zoning-checkbox-container">
                {luseList.length === 0 ? (
                  <div className="loading-zoning">載入用途區域列表中...</div>
                ) : (
                  <div className="zoning-checkbox-grid">
                    {luseList.map((luse) => (
                      <label key={luse} className="zoning-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newBuildingType.applicableZoning.includes(luse)}
                          onChange={() => handleZoningToggle(luse)}
                          className="zoning-checkbox"
                        />
                        <span>{luse}</span>
                      </label>
                    ))}
                  </div>
                )}
                {newBuildingType.applicableZoning.length > 0 && (
                  <div className="selected-zoning">
                    <strong>已選擇的用途區域（{newBuildingType.applicableZoning.length}個）：</strong>
                    <div className="selected-zoning-tags">
                      {newBuildingType.applicableZoning.map((zoning) => (
                        <span key={zoning} className="selected-zoning-tag">
                          {zoning}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleAddBuildingType}>
              新增
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowAddForm(false);
                setNewBuildingType({
                  code: "",
                  name: "",
                  description: "",
                  applicableZoning: [],
                });
              }}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">載入中...</div>
      ) : (
        <div className="building-types-table-wrapper">
          <table className="building-types-table">
            <thead>
              <tr>
                <th>代碼</th>
                <th>名稱</th>
                <th>說明</th>
                <th>適用用途區域</th>
                <th>工種數</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {buildingTypes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">
                    沒有註冊的建築類型。
                  </td>
                </tr>
              ) : (
                buildingTypes.map((type) => (
                  <tr key={type.code}>
                    <td className="code-cell">
                      <strong>{type.code}</strong>
                    </td>
                    <td>{type.name}</td>
                    <td>{type.description || "-"}</td>
                    <td>
                      {type.applicableZoning &&
                      type.applicableZoning.length > 0 ? (
                        <div className="zoning-tags">
                          {type.applicableZoning.map((zoning, index) => (
                            <span key={index} className="zoning-tag">
                              {zoning}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="count-cell">
                      {type._count?.costs || 0}個
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(type)}
                          title="修改"
                          disabled={loading || editingCode !== null || showAddForm}
                        >
                          ✎
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteBuildingType(type.code)}
                          title="刪除"
                          disabled={loading || editingCode !== null || showAddForm}
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BuildingTypeManagement;

